import { useCallback, useEffect, useMemo, useState } from 'react';

import { authService } from '@/services/auth.service';
import type { AuthScope, OAuthProvider, OAuthProviderStatus } from '@/types/auth';

interface UseAvailableSocialProvidersOptions {
  scope: AuthScope;
  tenantSlug?: string;
  skip?: boolean;
}

interface UseAvailableSocialProvidersResult {
  providers: OAuthProvider[];
  providerStatus: OAuthProviderStatus[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<OAuthProviderStatus[]>;
}

/**
 * Hook that resolves the list of enabled social login providers for a given scope.
 * It tolerates missing backend support by returning an empty array without
 * surfacing an error to the UI.
 */
export function useAvailableSocialProviders(
  options: UseAvailableSocialProvidersOptions,
): UseAvailableSocialProvidersResult {
  const { scope, tenantSlug, skip = false } = options;
  const [providerStatus, setProviderStatus] = useState<OAuthProviderStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProviders = useCallback(
    async (
      slug: string | undefined,
      control: { cancelled: boolean },
      { silent = false }: { silent?: boolean } = {},
    ): Promise<OAuthProviderStatus[]> => {
      if (skip || control.cancelled) {
        return [];
      }

      if (!silent) {
        setIsLoading(true);
      }

      try {
        const list = await authService.listAvailableOAuthProviders(scope, { tenantSlug: slug });
        if (!control.cancelled) {
          setProviderStatus(list);
          setError(null);
        }
        return list;
      } catch (err) {
        console.error('Failed to load social OAuth providers', err);
        if (!control.cancelled) {
          setProviderStatus([]);
          setError(err instanceof Error ? err.message : 'Failed to load social OAuth providers');
        }
        return [];
      } finally {
        if (!control.cancelled && !silent) {
          setIsLoading(false);
        }
      }
    },
    [scope, skip],
  );

  useEffect(() => {
    const control = { cancelled: false };

    void fetchProviders(tenantSlug, control);

    return () => {
      control.cancelled = true;
    };
  }, [fetchProviders, tenantSlug]);

  const refresh = useCallback(async () => {
    const control = { cancelled: false };
    return fetchProviders(tenantSlug, control, { silent: false });
  }, [fetchProviders, tenantSlug]);

  const providers = useMemo<OAuthProvider[]>(
    () => providerStatus.filter((item) => item.enabled).map((item) => item.provider),
    [providerStatus],
  );

  return {
    providers,
    providerStatus,
    isLoading,
    error,
    refresh,
  };
}
